import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth } from './AuthProvider';
import { useUnseenNotifications } from '../hooks/useUnseenNotifications';

interface IncomingServiceRequestsContextProps {
  /** Quote requests that arrived while nobody was looking at the list of them. */
  unseenCount: number;
  /** ServiceRequests.tsx reports this while its professional side is the one on screen. */
  setViewingIncomingRequests: (viewing: boolean) => void;
}

const IncomingServiceRequestsContext = createContext<IncomingServiceRequestsContextProps | undefined>(undefined);

/** What ServiceRequestsController sends the professional when somebody asks them for a price. */
const WATCHED_TYPES = ['NewServiceRequest'] as const;

/**
 * The same badge as incoming bookings, for the other half of the platform.
 *
 * It is a separate provider rather than one more type in the bookings one on
 * purpose: they light up different rows in the menu, and they clear on
 * different screens. A professional walking into their quote requests has not
 * seen the booking that arrived, and the badge must not say otherwise.
 *
 * Both sit on useUnseenNotifications, which is where the actual counting --
 * and the reason it is not route-based -- lives.
 */
export const IncomingServiceRequestsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { unseenCount, setViewing } = useUnseenNotifications(WATCHED_TYPES, isAuthenticated);

  return (
    <IncomingServiceRequestsContext.Provider
      value={{ unseenCount, setViewingIncomingRequests: setViewing }}
    >
      {children}
    </IncomingServiceRequestsContext.Provider>
  );
};

export const useIncomingServiceRequestsBadge = (): IncomingServiceRequestsContextProps => {
  const context = useContext(IncomingServiceRequestsContext);
  if (!context) throw new Error('useIncomingServiceRequestsBadge must be used within an IncomingServiceRequestsProvider');
  return context;
};
